import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Security as SecurityIcon,
  Person as PersonIcon,
  SportsHandball as SportsIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

interface CoachPermission {
  id: string;
  coach_id: string;
  permission_type: string;
  permission_value: string;
  granted_by: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
}

interface Coach {
  id: string;
  coach_id_code: string;
  first_name: string;
  last_name: string;
  coach_level: string;
  assigned_sports: string[];
}

const CoachPermissionsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [permissions, setPermissions] = useState<CoachPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grantPermissionOpen, setGrantPermissionOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  
  const [newPermission, setNewPermission] = useState({
    permissionType: 'sport_access',
    permissionValue: '',
    expiresAt: ''
  });

  useEffect(() => {
    if (user?.coachLevel === 'head_coach') {
      loadCoaches();
      loadPermissions();
    }
  }, [user]);

  const loadCoaches = async () => {
    try {
      const response = await api.get(`/coaches/hierarchy/${user?.coachId}`);
      if (response.data.success) {
        const { juniorCoaches } = response.data.data;
        setCoaches(juniorCoaches);
      }
    } catch (error) {
      console.error('Error loading coaches:', error);
      setError('Failed to load coaches');
    }
  };

  const loadPermissions = async () => {
    try {
      // TODO: Implement permissions API endpoint
      setPermissions([]);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const handleGrantPermission = async () => {
    try {
      setLoading(true);
      // TODO: Implement grant permission API
      setGrantPermissionOpen(false);
      setNewPermission({
        permissionType: 'sport_access',
        permissionValue: '',
        expiresAt: ''
      });
      loadPermissions();
    } catch (error) {
      console.error('Error granting permission:', error);
      setError('Failed to grant permission');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (permissionId: string, isActive: boolean) => {
    try {
      // TODO: Implement toggle permission API
      setPermissions(prev => 
        prev.map(p => p.id === permissionId ? { ...p, is_active: isActive } : p)
      );
    } catch (error) {
      console.error('Error toggling permission:', error);
      setError('Failed to update permission');
    }
  };

  const getPermissionIcon = (type: string) => {
    switch (type) {
      case 'sport_access': return <SportsIcon />;
      case 'player_access': return <PersonIcon />;
      case 'cross_sport': return <SecurityIcon />;
      default: return <SecurityIcon />;
    }
  };

  const getPermissionColor = (type: string) => {
    switch (type) {
      case 'sport_access': return 'primary';
      case 'player_access': return 'secondary';
      case 'cross_sport': return 'success';
      default: return 'default';
    }
  };

  if (user?.coachLevel !== 'head_coach') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Only head coaches can manage permissions.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Coach Permissions Management
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Coaches List */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Junior Coaches</Typography>
                <Button
                  variant="contained"
                  startIcon={<SecurityIcon />}
                  onClick={() => setGrantPermissionOpen(true)}
                >
                  Grant Permission
                </Button>
              </Box>
              
              <List>
                {coaches.map((coach) => (
                  <ListItem key={coach.id} divider>
                    <ListItemText
                      primary={`${coach.first_name} ${coach.last_name}`}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {coach.coach_id_code}
                          </Typography>
                          <Box display="flex" gap={1} mt={1}>
                            <Chip 
                              label={coach.coach_level.replace('_', ' ')} 
                              size="small"
                              color="secondary"
                            />
                            {coach.assigned_sports.map(sport => (
                              <Chip 
                                key={sport}
                                label={sport} 
                                size="small"
                                color="info"
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedCoach(coach);
                          setGrantPermissionOpen(true);
                        }}
                      >
                        Manage
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Permissions List */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Permissions
              </Typography>
              
              {permissions.length === 0 ? (
                <Typography color="text.secondary" align="center">
                  No permissions granted yet
                </Typography>
              ) : (
                <List>
                  {permissions.map((permission) => (
                    <ListItem key={permission.id} divider>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {getPermissionIcon(permission.permission_type)}
                            <Typography variant="subtitle2">
                              {permission.permission_type.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Chip 
                              label={permission.permission_value}
                              size="small"
                              color={getPermissionColor(permission.permission_type) as any}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Granted: {new Date(permission.granted_at).toLocaleDateString()}
                            {permission.expires_at && (
                              <> • Expires: {new Date(permission.expires_at).toLocaleDateString()}</>
                            )}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={permission.is_active}
                          onChange={(e) => handleTogglePermission(permission.id, e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grant Permission Dialog */}
      <Dialog open={grantPermissionOpen} onClose={() => setGrantPermissionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Permission</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Coach: {selectedCoach?.first_name} {selectedCoach?.last_name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Permission Type</InputLabel>
                <Select
                  value={newPermission.permissionType}
                  onChange={(e) => setNewPermission({ ...newPermission, permissionType: e.target.value })}
                >
                  <MenuItem value="sport_access">Sport Access</MenuItem>
                  <MenuItem value="player_access">Player Access</MenuItem>
                  <MenuItem value="cross_sport">Cross-Sport Access</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Permission Value"
                value={newPermission.permissionValue}
                onChange={(e) => setNewPermission({ ...newPermission, permissionValue: e.target.value })}
                placeholder="e.g., Badminton, Tennis, or 'all'"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expires At (Optional)"
                type="date"
                value={newPermission.expiresAt}
                onChange={(e) => setNewPermission({ ...newPermission, expiresAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantPermissionOpen(false)}>Cancel</Button>
          <Button onClick={handleGrantPermission} variant="contained" disabled={loading}>
            Grant Permission
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoachPermissionsPage;